<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type { ISODateTimeString } from '@gat-ship/types'
  import { Input } from '../ui/input'
  import { useDateInput } from './composables/useDateInput'
  import { useUserPreferences } from '../../composables/useUserPreferences'

  /**
   * Standalone date-input primitive — owns the `useDateInput` instance
   * and the `Input` element. Form-agnostic.
   *
   * It is a plain controlled component: the value comes in via
   * `modelValue` and edits go out via `update:modelValue` / `blur`. It
   * deliberately does NOT receive the tanstack `field` — `FieldApi.state`
   * is a plain object, not a Vue reactive source, so a `computed`
   * reading `field.state.value` here would never re-run after a commit
   * and the input would freeze. `FormDateField` reads the value inside
   * the reactive `form.Field` slot and passes it down as `modelValue`.
   */

  const props = defineProps<{
    /** The bound instant. An empty string is treated as no value. */
    modelValue?: ISODateTimeString | string
    /** Forwarded to the input's `id`. */
    name?: string
    placeholder?: string
    disabled?: boolean
    dataTestid?: string
    hasErrors?: boolean
    /**
     * When `true`, typing a lone day that has already passed this month
     * rolls it forward to the next month — for fields that should land in
     * the future. Off by default.
     */
    futureDate?: boolean
  }>()

  const emit = defineEmits<{
    'update:modelValue': [value: ISODateTimeString | undefined]
    blur: []
  }>()

  // The user's date-format preference drives both the display format and
  // how partial input is expanded; `useDateInput` falls back to the
  // runtime locale when it is unset.
  const getUserPreferences = useUserPreferences()
  const dateFormat = computed(() => getUserPreferences().dateFormat)

  /** The bound instant for the composable — empty string reads as unset. */
  function currentValue(): ISODateTimeString | undefined {
    const value = props.modelValue
    return value ? (value as ISODateTimeString) : undefined
  }

  const { dateInputValue, formatPattern, onFocus, onInput, onBlur } =
    useDateInput(
      {
        modelValue: currentValue,
        locale: dateFormat,
        futureDate: props.futureDate
      },
      (value) => {
        emit('update:modelValue', value)
      }
    )

  // The `Input` exposes its underlying `<input>` as `inputRef`; we read the
  // caret from it to decide whether to auto-mask (see `onControlInput`).
  const control = ref<InstanceType<typeof Input> | null>(null)

  // Only auto-mask an append (caret at the end). For a mid-string edit the
  // separators are left as-is so the caret and later digits don't shift; the
  // masking would otherwise repack the whole value and jump the cursor.
  function onControlInput(value: string) {
    const el = control.value?.inputRef ?? null
    const caretAtEnd =
      !el || el.selectionStart === null || el.selectionStart >= value.length
    onInput(value, caretAtEnd)
  }

  // Commit on blur, then let the parent run the field's blur-time
  // validation.
  function handleBlur() {
    onBlur()
    emit('blur')
  }
</script>

<template>
  <!-- `inputmode="numeric"` — a clean digits-only keypad. `useDateInput` masks
       the separators in as you type (`23042027` → `23.04.2027`), so no separator
       key is needed and the locale's `.`/`/`/`-` is always used. Desktop ignores
       inputmode; the masking applies there too. -->
  <Input
    :id="name"
    ref="control"
    :model-value="dateInputValue"
    :placeholder="placeholder ?? formatPattern"
    :data-testid="dataTestid"
    :disabled="disabled"
    :aria-invalid="hasErrors || undefined"
    inputmode="numeric"
    @focus="onFocus"
    @update:model-value="(value) => onControlInput(String(value))"
    @blur="handleBlur"
  />
</template>
